import { useState, useEffect } from "react";
import {
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Send,
  Eye,
  ChevronLeft,
  ChevronRight,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import {
  type AccessRequestItem,
  fetchAdminAccessRequests,
  approveAdminAccessRequest,
  rejectAdminAccessRequest,
  resendAdminInvitation,
} from "../../../api/accessRequestsApi";

interface AccessRequestsTableProps {
  onSelectRequest: (item: AccessRequestItem) => void;
  onCountsUpdated?: (counts: { pending: number; approved: number; rejected: number }) => void;
}

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export default function AccessRequestsTable({
  onSelectRequest,
  onCountsUpdated,
}: AccessRequestsTableProps) {
  const [requests, setRequests] = useState<AccessRequestItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Filters & Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const sortField = "created_at";
  const sortDirection = "desc";

  // In-flight action tracking
  const [actionInProgressId, setActionInProgressId] = useState<string | null>(null);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const res = await fetchAdminAccessRequests({
        search: search.trim() || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort: sortField,
        direction: sortDirection,
      });

      setRequests(res.items || []);
      setTotal(res.total || 0);

      if (onCountsUpdated) {
        onCountsUpdated({
          pending: res.pending_count,
          approved: res.approved_count,
          rejected: res.rejected_count,
        });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.detail || axiosErr.message || "Failed to load access requests.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [page, pageSize, search, statusFilter, sortField, sortDirection]);

  const handleQuickApprove = async (e: React.MouseEvent, item: AccessRequestItem) => {
    e.stopPropagation();
    if (!confirm(`Approve pre-launch access request for ${item.full_name} (${item.email})? A secure single-use invitation will be emailed.`)) {
      return;
    }

    setActionInProgressId(item.id);
    try {
      await approveAdminAccessRequest(item.id, {
        assigned_role: "client",
        assigned_plan: "starter",
      });
      toast.success(`Access request for ${item.full_name} approved! Invitation dispatched.`);
      loadRequests();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.detail || axiosErr.message || "Failed to approve request.");
    } finally {
      setActionInProgressId(null);
    }
  };

  const handleQuickReject = async (e: React.MouseEvent, item: AccessRequestItem) => {
    e.stopPropagation();
    const reason = prompt(`Enter optional rejection reason for ${item.full_name}:`, "");
    if (reason === null) return; // User cancelled

    setActionInProgressId(item.id);
    try {
      await rejectAdminAccessRequest(item.id, reason.trim() || undefined);
      toast.success(`Access request for ${item.full_name} rejected.`);
      loadRequests();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.detail || axiosErr.message || "Failed to reject request.");
    } finally {
      setActionInProgressId(null);
    }
  };

  const handleQuickResend = async (e: React.MouseEvent, item: AccessRequestItem) => {
    e.stopPropagation();
    setActionInProgressId(item.id);
    try {
      await resendAdminInvitation(item.id);
      toast.success(`Fresh invitation sent to ${item.email}.`);
      loadRequests();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.detail || axiosErr.message || "Failed to resend invitation.");
    } finally {
      setActionInProgressId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="access-table-card" id="access-requests-panel">
      {/* Table Toolbar */}
      <div className="table-toolbar">
        <div className="toolbar-search">
          <Search className="search-icon-pos" size={16} />
          <input
            type="text"
            placeholder="Search by applicant name, email, company, or role…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="toolbar-filters">
          <select
            className="select-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <button
            className="btn-secondary"
            onClick={() => loadRequests()}
            title="Refresh table data"
            style={{ padding: "8px 12px" }}
          >
            <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Table Component */}
      <div className="table-responsive">
        <table className="users-table">
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Applicant</th>
              <th style={{ width: "20%" }}>Organization & Role</th>
              <th style={{ width: "24%" }}>Reason / Scope</th>
              <th style={{ width: "12%" }}>Submitted</th>
              <th style={{ width: "10%" }}>Status</th>
              <th style={{ width: "12%", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && requests.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: "48px 0", color: "var(--color-text-muted)" }}>
                  <RefreshCw size={24} className="animate-spin" style={{ margin: "0 auto 12px auto" }} />
                  <div>Loading pre-launch access requests...</div>
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: "56px 0", color: "var(--color-text-muted)" }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text)", marginBottom: 6 }}>
                    No access requests found
                  </div>
                  <div style={{ fontSize: 13, maxWidth: 360, margin: "0 auto" }}>
                    {search || statusFilter !== "all"
                      ? "No records match your active search and filter criteria."
                      : "No access requests have been submitted by visitors yet."}
                  </div>
                </td>
              </tr>
            ) : (
              requests.map((item) => {
                const isPending = item.status === "pending";
                const isApproved = item.status === "approved";
                const isRejected = item.status === "rejected";
                const isProcessing = actionInProgressId === item.id;

                return (
                  <tr
                    key={item.id}
                    onClick={() => onSelectRequest(item)}
                    style={{ cursor: "pointer" }}
                    className="clickable-row"
                  >
                    {/* Applicant Name & Email */}
                    <td>
                      <div className="user-cell">
                        <div
                          className="user-avatar"
                          style={{
                            background: isApproved
                              ? "rgba(16, 185, 129, 0.15)"
                              : isRejected
                              ? "rgba(239, 68, 68, 0.15)"
                              : "rgba(245, 158, 11, 0.15)",
                            color: isApproved ? "#10b981" : isRejected ? "#ef4444" : "#f59e0b",
                            border: `1px solid ${
                              isApproved
                                ? "rgba(16, 185, 129, 0.3)"
                                : isRejected
                                ? "rgba(239, 68, 68, 0.3)"
                                : "rgba(245, 158, 11, 0.3)"
                            }`,
                          }}
                        >
                          {initials(item.full_name)}
                        </div>
                        <div className="user-info">
                          <span className="user-name">{item.full_name}</span>
                          <span className="user-email">{item.email}</span>
                        </div>
                      </div>
                    </td>

                    {/* Organization & Role */}
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontWeight: 600, color: "var(--color-text)", fontSize: 13 }}>
                          {item.company}
                        </span>
                        <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                          {item.role}
                        </span>
                      </div>
                    </td>

                    {/* Reason */}
                    <td>
                      <div
                        style={{
                          fontSize: 13,
                          color: "var(--color-text-muted)",
                          maxWidth: 320,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.reason}
                      >
                        {item.reason}
                      </div>
                    </td>

                    {/* Submitted Date */}
                    <td>
                      <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : "N/A"}
                      </div>
                    </td>

                    {/* Status */}
                    <td>
                      {isApproved ? (
                        <span className="status-badge active">
                          <CheckCircle size={12} /> Approved
                        </span>
                      ) : isRejected ? (
                        <span className="status-badge suspended">
                          <XCircle size={12} /> Rejected
                        </span>
                      ) : (
                        <span className="status-badge pending">
                          <Clock size={12} /> Pending
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {isPending && (
                          <>
                            <button
                              className="btn-primary"
                              onClick={(e) => handleQuickApprove(e, item)}
                              disabled={isProcessing}
                              title="Approve access and issue invitation"
                              style={{ padding: "5px 10px", fontSize: 12 }}
                            >
                              <CheckCircle size={13} /> Approve
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={(e) => handleQuickReject(e, item)}
                              disabled={isProcessing}
                              title="Reject access request"
                              style={{ padding: "5px 10px", fontSize: 12, color: "#ef4444" }}
                            >
                              <XCircle size={13} /> Reject
                            </button>
                          </>
                        )}

                        {isApproved && item.invitation_status !== "accepted" && (
                          <button
                            className="btn-secondary"
                            onClick={(e) => handleQuickResend(e, item)}
                            disabled={isProcessing}
                            title="Resend invitation email"
                            style={{ padding: "5px 10px", fontSize: 12 }}
                          >
                            <Send size={13} /> Resend
                          </button>
                        )}

                        <button
                          className="btn-secondary"
                          onClick={() => onSelectRequest(item)}
                          title="View application details"
                          style={{ padding: "5px 8px" }}
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="table-pagination">
        <div className="pagination-info">
          Showing{" "}
          <strong>
            {total === 0 ? 0 : (page - 1) * pageSize + 1} &ndash; {Math.min(page * pageSize, total)}
          </strong>{" "}
          of <strong>{total}</strong> access requests
        </div>

        <div className="pagination-controls">
          <select
            className="select-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </select>

          <button
            className="pagination-btn"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="pagination-page-indicator">
            {page} / {totalPages}
          </span>
          <button
            className="pagination-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
